/**
 * O desenho do mapa: os blocos (com título, resumo, grupo, saídas e posição)
 * e as ligações (com a saída, o rótulo e o tom), a partir da configuração.
 *
 * O mapa de leitura e o de edição desenham daqui, para o bloco ser o mesmo nos
 * dois: o de edição só acrescenta o que é da edição (escolhido, pendência,
 * simulação). Sem React e sem a biblioteca do mapa.
 */

import type { BlocosDoEditor } from '@cliqcrm/contracts';
import { grupoDoTipo, tomDaSaida, type TomDaSaida } from './aparencia';
import { resumoDoComeco } from './comeco';
import {
  PROXIMO,
  rotuloDaSaidaDoNo,
  saidaDaLigacao,
  saidasDoNo,
  type Configuracao,
} from './edicao';
import { ID_DO_COMECO, descreverNo, idDoOutroComeco, montarMapa, type GrupoDoBloco } from './mapa';

export type SaidaDesenhada = { id: string; rotulo: string; livre: boolean };

export type BlocoDesenhado = {
  id: string;
  tipo: string;
  grupo: GrupoDoBloco;
  titulo: string;
  resumo: string;
  saidas: SaidaDesenhada[];
  posicao: { x: number; y: number };
};

export type LigacaoDesenhada = {
  /** `bloco|saída`: uma saída leva a um bloco só. */
  id: string;
  de: string;
  saida: string;
  para: string;
  /** O nome da saída na ligação; `null` na continuação simples. */
  rotulo: string | null;
  tom: TomDaSaida;
};

/** Quanto o começo fica à esquerda do primeiro bloco. */
const RECUO_DO_COMECO = 300;
/** A distância entre dois começos que entram no mesmo bloco. */
const ALTURA_DO_COMECO = 130;

/** O bloco onde o outro começo entra: `null` é o primeiro bloco; vazio, desligado. */
function entradaDoOutroComeco(c: Configuracao, posicao: number): string {
  const entrada = c.outros_comecos[posicao]?.entrada;
  return entrada === null || entrada === undefined ? c.grafo.inicio : entrada;
}

/** Dá posição a todo bloco que ainda não tem, pela organização em árvore. */
export function comPosicoes(c: Configuracao, blocos: BlocosDoEditor | undefined): Configuracao {
  if (c.grafo.nos.every((n) => n.posicao)) return c;
  const mapa = montarMapa(c, blocos, '');
  const onde = new Map(mapa.blocos.map((b) => [b.id, { x: b.x, y: b.y }]));
  return {
    ...c,
    grafo: {
      ...c.grafo,
      nos: c.grafo.nos.map((n) => ({ ...n, posicao: n.posicao ?? onde.get(n.id)! })),
    },
  };
}

/** "Organizar": todo bloco ganha a posição da árvore, a partir do começo. */
export function organizarTudo(c: Configuracao, blocos: BlocosDoEditor | undefined): Configuracao {
  const sem = {
    ...c,
    grafo: {
      ...c.grafo,
      nos: c.grafo.nos.map(({ posicao: _fora, ...n }) => n as (typeof c.grafo.nos)[number]),
    },
  };
  return comPosicoes(sem, blocos);
}

export function desenharMapa(
  config: Configuracao,
  blocos: BlocosDoEditor | undefined,
  casosNovos: Readonly<Record<string, readonly string[]>> = {},
): { blocos: BlocoDesenhado[]; ligacoes: LigacaoDesenhada[] } {
  const c = comPosicoes(config, blocos);
  const ligadas = new Set(c.grafo.ligacoes.map((l) => `${l.de}|${saidaDaLigacao(l)}`));

  const comeco = montarMapa(
    { grafo: null, condicao: c.condicao },
    blocos,
    resumoDoComeco(c.gatilho, blocos),
  ).blocos[0]!;
  const primeiro = c.grafo.nos.find((n) => n.id === c.grafo.inicio);
  const xs = c.grafo.nos.map((n) => n.posicao?.x ?? 0);

  const desenhados: BlocoDesenhado[] = [
    {
      id: ID_DO_COMECO,
      tipo: 'comeco',
      grupo: 'comeco',
      titulo: comeco.titulo,
      resumo: comeco.resumo,
      saidas: [{ id: PROXIMO, rotulo: 'Começa', livre: !c.grafo.inicio }],
      posicao: {
        x: (primeiro?.posicao?.x ?? (xs.length ? Math.min(...xs) : 340)) - RECUO_DO_COMECO,
        y: primeiro?.posicao?.y ?? 40,
      },
    },
    // Os outros começos (#1183): à esquerda do bloco onde entram, um embaixo do outro.
    ...c.outros_comecos.map((o, i) => {
      const entrada = entradaDoOutroComeco(c, i);
      const alvo = c.grafo.nos.find((n) => n.id === entrada);
      const antes = c.outros_comecos
        .slice(0, i)
        .filter((_, j) => entradaDoOutroComeco(c, j) === entrada).length;
      // Uma linha abaixo do bloco onde entra: à esquerda dele, na mesma linha, costuma
      // estar o bloco anterior do fluxo (ou o começo principal, no primeiro).
      const abaixo = antes + 1;
      const resumo = resumoDoComeco({ tipo: 'evento', evento: o.evento }, blocos);
      return {
        id: idDoOutroComeco(i),
        tipo: 'comeco',
        grupo: 'comeco' as const,
        titulo: 'Também começa quando',
        resumo: o.condicao ? `${resumo}. Só se: ${o.condicao.expressao.slice(0, 40)}` : resumo,
        saidas: [{ id: PROXIMO, rotulo: 'Começa', livre: !entrada }],
        posicao: {
          x: (alvo?.posicao?.x ?? (xs.length ? Math.min(...xs) : 340)) - RECUO_DO_COMECO,
          y: (alvo?.posicao?.y ?? 40) + abaixo * ALTURA_DO_COMECO,
        },
      };
    }),
    ...c.grafo.nos.map((no) => ({
      id: no.id,
      tipo: no.tipo,
      grupo: grupoDoTipo(no.tipo),
      ...descreverNo(no, blocos),
      saidas: saidasDoNo(no, c.grafo.ligacoes, blocos, casosNovos[no.id]).map((s) => ({
        id: s,
        rotulo: rotuloDaSaidaDoNo(no, s),
        livre: !ligadas.has(`${no.id}|${s}`),
      })),
      posicao: no.posicao ?? { x: 0, y: 0 },
    })),
  ];

  const ligacoes: LigacaoDesenhada[] = [
    ...(c.grafo.inicio
      ? [
          {
            id: `${ID_DO_COMECO}|${PROXIMO}`,
            de: ID_DO_COMECO,
            saida: PROXIMO,
            para: c.grafo.inicio,
            rotulo: null,
            tom: 'padrao' as const,
          },
        ]
      : []),
    ...c.outros_comecos.flatMap((_, i) => {
      const para = entradaDoOutroComeco(c, i);
      return para
        ? [
            {
              id: `${idDoOutroComeco(i)}|${PROXIMO}`,
              de: idDoOutroComeco(i),
              saida: PROXIMO,
              para,
              rotulo: null,
              tom: 'padrao' as const,
            },
          ]
        : [];
    }),
    ...c.grafo.ligacoes.map((l) => {
      const saida = saidaDaLigacao(l);
      const de = c.grafo.nos.find((n) => n.id === l.de);
      const tipo = de?.tipo ?? '';
      return {
        id: `${l.de}|${saida}`,
        de: l.de,
        saida,
        para: l.para,
        // A continuação só tem nome na escolha, "Outro caso".
        rotulo: saida !== PROXIMO || tipo === 'escolha' ? rotuloDaSaidaDoNo(de, saida) : null,
        tom: tomDaSaida(saida),
      };
    }),
  ];

  return { blocos: desenhados, ligacoes };
}
